import React, {useCallback, useMemo, useState} from 'react';
import {Button, Flex} from '@tempo/core';
import {getUserDefaultRoute} from '@deezer/next/shared/utils/user';
import UserRights from '@deezer/next/features/Account/components/UserRights';
import UserName from '@deezer/next/features/Account/components/UserName';
import UserMail from '@deezer/next/features/Account/components/UserMail';
import UserPassword from '@deezer/next/features/Account/components/UserPassword';
import {useTranslation} from 'next-i18next';
import type {MyUser} from '@deezer/next/contexts/main/types';
import {useRouter} from 'next/router';
import passwordValidator from '@deezer/next/shared/utils/password/validator';
import {emailRegex} from '@constants/regex';
import UserLanguage from '@deezer/next/features/Account/components/UserLanguage';
import UserThemeToggle from '@deezer/next/features/Account/components/UserThemeToggle';
import type {UserForm as UserFormType} from '@deezer/next/features/Account/type';
import {MODAL_STATE} from '@deezer/next/features/Account/type';
import EditPasswordModal from '@deezer/next/features/Account/components/modals/EditPasswordModal';
import EditEmailModal from '@deezer/next/features/Account/components/modals/EditEmailModal';
import DeleteConnectedAccountsModal from '@deezer/next/features/Account/components/modals/DeleteConnectedAccountsModal';
import UserPasswordForm from '@deezer/next/features/Account/components/UserPasswordForm';
import UserEmailForm from '@deezer/next/features/Account/components/UserEmailForm';
import StarmaniaApi from '@deezer/next/services/api/Starmania';
import {onTokenUpdate} from '@deezer/next/contexts/main/effects';
import LegacyApi from '@deezer/next/services/api/Legacy';
import {
	useActiveArtist,
	useFetchUserMe,
	useLogout,
} from '@deezer/next/contexts/main/hooks';
import useToast from '@deezer/next/shared/hooks/useToast';
import {AuthProvider} from '@deezer/next/services/api/Starmania/requests/auth/types';
import DeleteAccountModal from '@deezer/next/features/Account/components/modals/DeleteAccountModal';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import FooterRow from '@components/PageElements/FooterRow';
import FormSectionTitle from '@components/PageElements/FormSectionTitle';
import {Routes} from '@constants/routes';
import FormContainer from '@components/PageElements/FormContainer';
import FormSection from '@components/PageElements/FormSection';
import FormSectionContainer from '@components/PageElements/FormSectionContainer';

type UserFormProps = {
	action: 'create' | 'edit';
	title: string;
	user: UserFormType;
	isCurrentUser: boolean;
	isEditAllowed: boolean;
};

const UserForm = ({
	action,
	title,
	user,
	isCurrentUser,
	isEditAllowed,
}: UserFormProps) => {
	const {t} = useTranslation('d4c');
	const toast = useToast();
	const fetchUserMe = useFetchUserMe();
	const logout = useLogout();
	const [formUser, setFormUser] = useState<UserFormType>(user);
	const [isValidUser, setIsValidUser] = useState(action === 'edit');
	const [modalOpen, setModalOpen] = useState(false);
	const [modalState, setModalState] = useState<MODAL_STATE | undefined>(
		undefined,
	);
	const router = useRouter();
	const activeArtist = useActiveArtist();

	const refreshUser = useCallback(
		async (token?: string) => {
			if (token) {
				onTokenUpdate(token);
				await LegacyApi.user.resetSession();
			}
			await fetchUserMe();
		},
		[fetchUserMe],
	);

	const handleFormUserChange = useCallback(
		(value: Partial<UserFormType>, isValid?: boolean) => {
			setFormUser(prev => (prev ? {...prev, ...value} : formUser));
			if (typeof isValid === 'boolean') {
				setIsValidUser(isValid);
			}
		},
		[formUser],
	);

	const handleOpenModal = useCallback((state: MODAL_STATE) => {
		setModalState(state);
		setModalOpen(true);
	}, []);

	const handleCloseModal = useCallback(() => {
		setModalOpen(false);
		setModalState(undefined);
	}, []);

	const handleBack = useCallback(() => {
		if (isCurrentUser) {
			const route = getUserDefaultRoute(user as MyUser, activeArtist);
			router.push(route);
		} else {
			router.back();
		}
	}, [isCurrentUser, router, user, activeArtist]);

	const handleSave = useCallback(async () => {
		try {
			const result = await StarmaniaApi.users.patchUser(String(formUser.id), {
				...formUser,
				password: formUser.password || undefined,
			});
			await refreshUser(result.data.newToken);
			handleCloseModal();
			toast({
				title: t('toastmessage_title_accountupdated_d4c-web'),
				description: t('toastmessage_text_accountupdated_d4c-web'),
				status: 'success',
			});
		} catch (err: any) {
			toast({
				title: t('toastmessage_title_unabletoupdateaccount_d4c-web'),
				description: err.response.data.errors?.[0]?.detail,
				status: 'error',
			});
		}
	}, [formUser, refreshUser, handleCloseModal, t, toast]);

	const handleSubmit = useCallback(async () => {
		const successTitle =
			action === 'create'
				? t('toastmessage_title_accountcreated_d4c-web')
				: t('toastmessage_title_accountupdated_d4c-web');
		const successDescription =
			action === 'create'
				? t('toastmessage_text_accountcreated_d4c-web')
				: t('toastmessage_text_accountupdated_d4c-web');
		const errorTitle =
			action === 'create'
				? t('toastmessage_title_unabletocreateaccount_d4c-web')
				: t('toastmessage_title_unabletoupdateaccount_d4c-web');

		try {
			if (action === 'create') {
				await StarmaniaApi.users.createUser(formUser);
			} else {
				const result = await StarmaniaApi.users.patchUser(String(formUser.id), {
					...formUser,
					password: formUser.password || undefined,
				});
				if (isCurrentUser) {
					await refreshUser(result.data.newToken);
				}
			}

			handleBack();
			toast({
				title: successTitle,
				description: successDescription,
				status: 'success',
			});
		} catch (err: any) {
			toast({
				title: errorTitle,
				description: err.response.data.errors?.[0]?.detail,
				status: 'error',
			});
		}
	}, [formUser, refreshUser, isCurrentUser, action, handleBack, t, toast]);

	const handleDelete = useCallback(
		async (id?: string) => {
			const userId = isCurrentUser ? user.id : id;
			if (!userId) return;

			try {
				await StarmaniaApi.users.deleteUser(String(userId));
				toast({
					title: isCurrentUser
						? 'Your account has been successfully deleted'
						: t('toastmessage_title_userdeletedsuccessfully_d4c-web'),
					status: 'success',
				});
				if (isCurrentUser) {
					await logout({replace: false});
				}
			} catch (e: any) {
				toast({
					title: isCurrentUser
						? 'Unable to delete your account. Try again later'
						: t('toastmessage_title_unabletodeletethisuser_d4c-web'),
					description: e.response.data.errors?.[0]?.detail,
					status: 'error',
				});
			}
		},
		[isCurrentUser, user.id, logout, toast, t],
	);

	const modalContent = useMemo(() => {
		switch (modalState) {
			case MODAL_STATE.EDIT_PASSWORD:
				return (
					<EditPasswordModal
						action={action}
						password={formUser.password || ''}
						confirmationPassword={formUser.confirmationPassword || ''}
						currentPassword={formUser.currentPassword || ''}
						isDisabled={!isEditAllowed && !isCurrentUser}
						isRequired={action === 'create'}
						isCurrentUser={isCurrentUser}
						isOpen={modalOpen}
						onChange={handleFormUserChange}
						onValidationPassword={setIsValidUser}
						onClose={handleCloseModal}
						onSave={handleSave}
					/>
				);
			case MODAL_STATE.EDIT_EMAIL:
				return (
					<EditEmailModal
						data={user.email || ''}
						isOpen={modalOpen}
						onClose={handleCloseModal}
						onChange={handleFormUserChange}
						onSave={handleSave}
					/>
				);
			case MODAL_STATE.DELETE_CONNECTED_ACCOUNTS:
				return (
					<DeleteConnectedAccountsModal
						isOpen={modalOpen}
						onClose={handleCloseModal}
						onDelete={handleDelete}
					/>
				);
			case MODAL_STATE.DELETE_ACCOUNT:
				return (
					<DeleteAccountModal
						isCurrentUser={isCurrentUser}
						isOpen={modalOpen}
						onClose={handleCloseModal}
						onDelete={handleDelete}
					/>
				);
			default:
				return null;
		}
	}, [
		formUser,
		modalState,
		user,
		action,
		handleCloseModal,
		handleDelete,
		isCurrentUser,
		isEditAllowed,
		modalOpen,
		handleSave,
		handleFormUserChange,
		setIsValidUser,
	]);

	const canSubmit = useMemo(() => {
		const password = formUser.password || '';
		const validName =
			formUser && formUser.name.length > 0 && formUser.name.length < 100;
		const validEmail = formUser && emailRegex.test(formUser.email);
		const validPassword =
			(action === 'edit' && !password) || !passwordValidator(password).warning;

		return validName && validEmail && validPassword && isValidUser;
	}, [formUser, isValidUser, action]);

	return (
		<PageLayout
			header={
				<TitleRow
					title={title}
					data-testid={
						isCurrentUser ? 'my-profile-title' : 'user-manager-form-title'
					}
					breadcrumbs={
						isCurrentUser
							? undefined
							: [
									{
										key: 'user-manager',
										label: t('usermanager_title_userslist_d4c-web'),
										url: Routes.USERS,
									},
								]
					}
				/>
			}
			footer={
				<FooterRow
					rightElements={
						<Button
							data-testid={'user-manager-form-submit-button'}
							isDisabled={!canSubmit}
							onClick={handleSubmit}
							size="small"
						>
							{action === 'edit'
								? t('generic_action_save_d4c-web')
								: t('generic_action_create_d4c-web')}
						</Button>
					}
				/>
			}
		>
			<FormContainer>
				{isCurrentUser && (
					<Flex w="100%" justifyContent="flex-end">
						<UserThemeToggle />
					</Flex>
				)}
				<FormSection>
					<FormSectionTitle title={t('usermanager_title_userinfo_d4c-web')} />
					<FormSectionContainer>
						<UserName
							data={user.name || ''}
							isDisabled={!isEditAllowed && !isCurrentUser}
							isRequired={true}
							onChange={handleFormUserChange}
						/>
						{action === 'create' || (action === 'edit' && !isCurrentUser) ? (
							<UserEmailForm
								data={user.email || ''}
								onChange={handleFormUserChange}
								isDisabled={
									(!isEditAllowed && !isCurrentUser) ||
									(isCurrentUser &&
										(user as MyUser).authProvider === AuthProvider.OKTA)
								}
								isRequired={action === 'create'}
							/>
						) : (
							<UserMail
								onModalOpen={handleOpenModal}
								data={user.email || ''}
								isDisabled={true}
								canUpdate={(user as MyUser).authProvider !== AuthProvider.OKTA}
							/>
						)}
						{action === 'create' ? (
							<UserPasswordForm
								data={user.password || ''}
								isDisabled={
									(!isEditAllowed && !isCurrentUser) ||
									(isCurrentUser &&
										(user as MyUser).authProvider === AuthProvider.OKTA)
								}
								isRequired={action === 'create'}
								isCurrentUser={isCurrentUser}
								onChange={handleFormUserChange}
								onPasswordConfirmationChange={setIsValidUser}
							/>
						) : (
							isCurrentUser && (
								<UserPassword
									onModalOpen={handleOpenModal}
									isDisabled={true}
									canUpdate={
										(user as MyUser).authProvider !== AuthProvider.OKTA
									}
								/>
							)
						)}
					</FormSectionContainer>
				</FormSection>

				<FormSection>
					<FormSectionTitle title={t('usermanager_title_rights_d4c-web')} />
					<FormSectionContainer>
						<UserRights
							data={user.features}
							showHiddenRights={isCurrentUser}
							isDisabled={
								!isEditAllowed ||
								isCurrentUser ||
								(isCurrentUser &&
									(user as MyUser).authProvider === AuthProvider.OKTA)
							}
							onChange={handleFormUserChange}
						/>
					</FormSectionContainer>
				</FormSection>

				{action === 'edit' && isCurrentUser && (
					<FormSection>
						<FormSectionTitle
							title={t('profilesettings_title_language_d4c-web')}
						/>
						<FormSectionContainer>
							<UserLanguage />
						</FormSectionContainer>
					</FormSection>
				)}

				{action === 'edit' &&
					isCurrentUser &&
					(user as MyUser).authProvider !== AuthProvider.OKTA && (
						<FormSection>
							{/*fixme translate*/}
							<FormSectionTitle
								title={'Danger section'}
								color={'text.feedback.error.default'}
								subtitle={
									'By deleting your account, you also remove all connected claims associated to this account.'
								}
							/>
							<FormSectionContainer>
								<Button
									variant={'ghost'}
									color={'text.feedback.error.default'}
									onClick={() => handleOpenModal(MODAL_STATE.DELETE_ACCOUNT)}
									width="fit-content"
								>
									{/*fixme translate*/}
									Delete account
								</Button>
							</FormSectionContainer>
						</FormSection>
					)}
			</FormContainer>
			{modalOpen && modalContent}
		</PageLayout>
	);
};

export default UserForm;
